// src/lib/stock-shape-svgs.tsx
import type { ReactNode } from 'react'

type Variant = 'glyph' | 'card' | 'full'

// Size mapping: glyph 24px, card 96px, full 200px
function sizeFor(v: Variant) {
  return v === 'glyph' ? 24 : v === 'card' ? 96 : 200
}

const stroke = 'stroke-current'
const callout = 'text-cyan-500 fill-current'

export function RectangularSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <rect x="20" y="25" width="60" height="50" fill="none" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="20" textAnchor="middle">X={values?.x ?? '—'}</text>
          <text x="50" y="92" textAnchor="middle">Y={values?.y ?? '—'}</text>
          <text x="88" y="52" textAnchor="start">Z={values?.z ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function RoundBarSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <ellipse cx="50" cy="35" rx="30" ry="8" fill="none" strokeWidth="2" className={stroke} />
      <line x1="20" y1="35" x2="20" y2="75" strokeWidth="2" className={stroke} />
      <line x1="80" y1="35" x2="80" y2="75" strokeWidth="2" className={stroke} />
      <ellipse cx="50" cy="75" rx="30" ry="8" fill="none" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="50" textAnchor="middle">Ø{values?.diameter ?? '—'}</text>
          <text x="90" y="58" textAnchor="start">L={values?.length ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function HexBarSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  // Hex prism: top hex, side lines, bottom hex
  const hex = (cy: number) =>
    `M30,${cy} L40,${cy - 8} L60,${cy - 8} L70,${cy} L60,${cy + 8} L40,${cy + 8} Z`
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <path d={hex(35)} fill="none" strokeWidth="2" className={stroke} />
      <line x1="30" y1="35" x2="30" y2="75" strokeWidth="2" className={stroke} />
      <line x1="70" y1="35" x2="70" y2="75" strokeWidth="2" className={stroke} />
      <line x1="50" y1="43" x2="50" y2="83" strokeWidth="2" className={stroke} />
      <path d={hex(75)} fill="none" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="55" textAnchor="middle">AF={values?.af ?? '—'}</text>
          <text x="90" y="58" textAnchor="start">L={values?.length ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function PlateSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <rect x="15" y="45" width="70" height="10" fill="none" strokeWidth="2" className={stroke} />
      <line x1="15" y1="45" x2="22" y2="38" strokeWidth="2" className={stroke} />
      <line x1="85" y1="45" x2="92" y2="38" strokeWidth="2" className={stroke} />
      <line x1="22" y1="38" x2="92" y2="38" strokeWidth="2" className={stroke} />
      <line x1="92" y1="38" x2="92" y2="48" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="70" textAnchor="middle">X={values?.x ?? '—'} × Y={values?.y ?? '—'}</text>
          <text x="50" y="82" textAnchor="middle">t={values?.thickness ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function TubeSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <ellipse cx="50" cy="35" rx="30" ry="8" fill="none" strokeWidth="2" className={stroke} />
      <ellipse cx="50" cy="35" rx="20" ry="5" fill="none" strokeWidth="2" className={stroke} />
      <line x1="20" y1="35" x2="20" y2="75" strokeWidth="2" className={stroke} />
      <line x1="80" y1="35" x2="80" y2="75" strokeWidth="2" className={stroke} />
      <ellipse cx="50" cy="75" rx="30" ry="8" fill="none" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="50" textAnchor="middle">
            OD{values?.od ?? '—'} ID{values?.id ?? '—'}
          </text>
          <text x="90" y="58" textAnchor="start">L={values?.length ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function LAngleSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <path d="M25,25 L25,75 L75,75 L75,67 L33,67 L33,25 Z"
            fill="none" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="20" y="50" textAnchor="end">A={values?.legA ?? '—'}</text>
          <text x="50" y="90" textAnchor="middle">B={values?.legB ?? '—'}</text>
          <text x="80" y="50" textAnchor="start">t={values?.thickness ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function TSectionSvg({ variant, values }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <path d="M20,25 L80,25 L80,38 L57,38 L57,80 L43,80 L43,38 L20,38 Z"
            fill="none" strokeWidth="2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="20" textAnchor="middle">fW={values?.flangeW ?? '—'}</text>
          <text x="85" y="60" textAnchor="start">wH={values?.webH ?? '—'}</text>
        </g>
      )}
    </svg>
  )
}

export function IrregularDrawingSvg({ variant }:
  { variant: Variant; values?: Record<string, number> }): ReactNode {
  const s = sizeFor(variant)
  return (
    <svg viewBox="0 0 100 100" width={s} height={s} className="text-slate-400">
      <path d="M30,30 Q20,40 25,55 Q22,70 35,75 Q50,85 65,75 Q80,72 78,55 Q85,42 70,32 Q55,22 30,30 Z"
            fill="none" strokeWidth="2" strokeDasharray="3 2" className={stroke} />
      {variant === 'full' && (
        <g className={callout} fontSize="8" fontFamily="monospace">
          <text x="50" y="93" textAnchor="middle">from stock drawing</text>
        </g>
      )}
    </svg>
  )
}

